// Coupon wording shared by the admin Coupons page and the shopper's cart. Amounts come from the server; the
// discount is always on the items total, never the delivery.
import { formatRupee } from '../components/Price'

export const COUPON_TYPES = [
    { value: 'PERCENT', label: '% off' },
    { value: 'AMOUNT', label: '₹ off' },
    { value: 'NONE', label: 'None' }, // free delivery only
]

export const SCOPES = [
    { value: 'ALL_ITEMS', label: 'All items', hint: 'Off the items total' },
    { value: 'ONE_ITEM', label: 'One item', hint: 'One piece of the priciest item' },
]

export const AUDIENCES = [
    { value: 'ALL', label: 'Everyone', hint: 'Applied automatically for every shopper' },
    { value: 'SELECTED', label: 'Selected shoppers', hint: 'Applied automatically for the shoppers you choose' },
    { value: 'CODE', label: 'Anyone with the code', hint: 'Shoppers type the code at checkout' },
]

export const STATUS_LABELS = {
    ACTIVE: 'Active',
    SCHEDULED: 'Scheduled',
    EXPIRED: 'Expired',
    USED_UP: 'Fully used',
    OFF: 'Off',
}

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v))

const trimZeros = (v) => String(Number(v))

// "20% OFF" / "₹200 OFF" / "FREE DELIVERY" (big ticket value)
export const couponValue = (c) => {
    if (c.discountType === 'NONE') return 'FREE DELIVERY'
    const value = num(c.discountValue)
    if (value === null || Number.isNaN(value)) return c.discountType === 'PERCENT' ? '–% OFF' : '₹– OFF'
    return c.discountType === 'PERCENT' ? `${trimZeros(value)}% OFF` : `${formatRupee(value)} OFF`
}

// "On 1 item · Up to ₹300 · Free delivery · Min order ₹999" (or "No minimum order")
export const couponTerms = (c) => {
    const parts = []
    const cap = num(c.maxDiscount)
    const min = num(c.minOrderAmount)
    if (c.discountType !== 'NONE' && c.appliesTo === 'ONE_ITEM') parts.push('On 1 item')
    if (c.discountType === 'PERCENT' && cap > 0) parts.push(`Up to ${formatRupee(cap)}`)
    if (c.freeDelivery && c.discountType !== 'NONE') parts.push('Free delivery')
    parts.push(min > 0 ? `Min order ${formatRupee(min)}` : 'No minimum order')
    return parts.join(' · ')
}

// one sentence for the preview: "20% off one item (the priciest) up to ₹300 + free delivery on orders of ₹999 or more,
// once per shopper."
export const couponSentence = (c) => {
    const min = num(c.minOrderAmount)
    if (c.discountType === 'NONE') {
        if (!c.freeDelivery) return 'Turn on free delivery, or choose a discount.'
        return 'Free delivery' + (min > 0 ? ` on orders of ${formatRupee(min)} or more` : ' on any order') + ', once per shopper.'
    }
    const value = num(c.discountValue)
    if (!value) return 'Set the discount to see how shoppers will read this coupon.'
    const cap = num(c.maxDiscount)
    let text = c.discountType === 'PERCENT' ? `${trimZeros(value)}% off` : `${formatRupee(value)} off`
    if (c.appliesTo === 'ONE_ITEM') text += ' one item (the priciest)'
    if (c.discountType === 'PERCENT' && cap > 0) text += ` up to ${formatRupee(cap)}`
    if (c.freeDelivery) text += ' + free delivery'
    text += min > 0 ? ` on orders of ${formatRupee(min)} or more` : ' on any order'
    return text + ', once per shopper.'
}

const DAY = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' })
const DAY_YEAR = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })

// ISO "2026-10-31" → "31 Oct" (adds the year when it isn't this year)
export const formatDay = (iso) => {
    if (!iso) return ''
    const [y, m, d] = String(iso).split('-').map(Number)
    const date = new Date(y, m - 1, d)
    return (y === new Date().getFullYear() ? DAY : DAY_YEAR).format(date)
}

// "Till 31 Oct", "Starts 12 Oct", "Ended 2 Oct", "No end date"
export const couponDates = (c) => {
    if (c.status === 'SCHEDULED' && c.startsOn) return `Starts ${formatDay(c.startsOn)}`
    if (c.status === 'EXPIRED' && c.endsOn) return `Ended ${formatDay(c.endsOn)}`
    if (c.endsOn) return `Till ${formatDay(c.endsOn)}`
    return 'No end date'
}

// the coupon fields of GET /cart (null when there is none). saving = discount + the delivery it waives.
export const cartCoupon = (data) => {
    const discount = Number(data.couponDiscount || 0)
    const freeDelivery = !!data.couponCode && !!data.couponFreeDelivery
    return {
        code: data.couponCode || null,
        name: data.couponName || null,
        discount,
        freeDelivery,
        itemName: data.couponItemName || '',
        saving: discount + (freeDelivery ? Number(data.deliveryCharges || 0) : 0),
        auto: !!data.couponAuto,
        message: data.couponMessage || '',
        error: data.couponError || '',
    }
}

// random readable code (no 0/O/1/I), e.g. for the Generate button
export const randomCode = (prefix = '') => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
    const clean = String(prefix).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10)
    let code = clean
    const values = new Uint32Array(6)
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(values)
    else values.forEach((_, i) => { values[i] = Math.floor(Math.random() * 1e9) })
    for (let i = 0; code.length < Math.max(clean.length + 4, 6); i++) code += chars[values[i % values.length] % chars.length]
    return code
}
